// useAgendaListPrint.ts - Custom hook for agenda list PDF export
// Encapsulates PDF generation state and action for the agenda list view.
// Exposes handlePrint(), isPrinting, and canPrint based on current data state.
// CREATED: PDF export support for the list/agenda view in TeamView
// FIXED: handlePrint now guards against concurrent invocations via isPrinting check

import { useState, useCallback } from 'react';
import { generateAgendaListPdf } from '../lib/agendaListPdf';
import { captureException } from '../lib/sentry';
import type { EnrichedMission } from '../components/manager/agenda/types';
import type { Service } from '../lib/services';

interface UseAgendaListPrintOptions {
  siteName: string;
  periodLabel: string;
  tasks: EnrichedMission[];
  services: Service[];
}

export function useAgendaListPrint(options: UseAgendaListPrintOptions) {
  const [isPrinting, setIsPrinting] = useState(false);

  const handlePrint = useCallback(async () => {
    if (isPrinting) return;
    if (options.tasks.length === 0) return;

    setIsPrinting(true);
    await new Promise<void>(resolve => setTimeout(resolve, 50));
    try {
      await generateAgendaListPdf({
        siteName: options.siteName,
        periodLabel: options.periodLabel,
        tasks: options.tasks,
        services: options.services,
      });
    } catch (error) {
      if (import.meta.env.DEV) console.error('Agenda list PDF generation failed:', error);
      captureException(error, { context: 'useAgendaListPrint' });
    } finally {
      setIsPrinting(false);
    }
  }, [isPrinting, options.siteName, options.periodLabel, options.tasks, options.services]);

  const canPrint = options.tasks.length > 0;

  return { handlePrint, isPrinting, canPrint };
}
